import { CompanyItem, LocalizedText } from "@/common/common";
import { DecisionType } from "@/common/decisions";
import { DocumentType } from "@/common/documents";
import { PageFormat } from "@/components-v2/DataGraph/placement/layoutOptions";
import { OwnershipGraphType } from "@/overview/ownership/commonOwnershipDomain";
import { GraphNodeSettings, OwnershipGraph } from "@/overview/ownership/ownershipDomain";
import { OwnershipFilterGroup } from "@/overview/ownership/ownershipFilterDomain";
import type { FeatureToggles } from "@/common/featureToggles";
import type { EnterpriseAccountInfo } from "@/types/CompanyInfo";
import type { TMessageKey } from "@/i18n/types.gen";

export enum TemplateType {
  PROJECT = "PROJECT",
  MAP = "MAP",
}

export const templateTypeSegments: { [type in TemplateType]: string } = {
  [TemplateType.PROJECT]: "dbot",
  [TemplateType.MAP]: "map",
};

export type TemplateContent = ProjectTemplateContent | MapTemplateContent;

export type ProjectTemplateContent = {
  type: TemplateType.PROJECT;
  matters: ProjectTemplateMatterContent[];
  recipients: ProjectTemplateRecipient[];
};

export type MapTemplateContent = {
  type: TemplateType.MAP;
  settings: MapSettings;
  graph: OwnershipGraph;
};

export enum MapTemplateSetting {
  FORMAT = "FORMAT",
  FILTERS = "FILTERS",
  NODE_SETTINGS = "NODE_SETTINGS",
  THRESHOLD = "THRESHOLD",
  MAX_ENTITIES = "MAX_ENTITIES",
}

export type MapTemplateGraphType =
  | OwnershipGraphType.owner
  | OwnershipGraphType.owns
  | OwnershipGraphType.dual;

export type MapSettings = {
  graphType: MapTemplateGraphType;
  values: MapSettingValues;
};

export type MapSettingValues = {
  [MapTemplateSetting.FORMAT]?: PageFormat;
  [MapTemplateSetting.FILTERS]?: OwnershipFilterGroup;
  [MapTemplateSetting.NODE_SETTINGS]?: { [id: string]: GraphNodeSettings };
  [MapTemplateSetting.THRESHOLD]?: number;
  [MapTemplateSetting.MAX_ENTITIES]?: number;
};

export type ProjectTemplateRecipientCategory = "SHAREHOLDER" | "BOARD_MEMBER";

export type ProjectTemplateRecipient = {
  id: string;
  name: string;
  category: ProjectTemplateRecipientCategory;
};

export type ProjectTemplateMatterContent = { decisionId: string; rows: string[][] };

export type SaveTemplateRequest = {
  enterpriseAccountId: string;
  name: string;
  description?: string;
  content: SaveTemplateContentRequest;
};

export type UpdateTemplateRequest = {
  name: string;
  description?: string;
  content: UpdateTemplateContentRequest;
};

export type UpdateTemplateContentRequest =
  | UpdateProjectTemplateContentRequest
  | UpdateMapTemplateContentRequest;

export type SaveTemplateContentRequest =
  | SaveProjectTemplateContentRequest
  | SaveMapTemplateContentRequest;

export type SaveProjectTemplateContentRequest = UpdateProjectTemplateContentRequest & {
  projectId: string;
};

export type SaveMapTemplateContentRequest = {
  type: TemplateType.MAP;
  companyId: string;
  settings: MapSettings;
};

export type UpdateProjectTemplateContentRequest = {
  type: TemplateType.PROJECT;
  decisionIds: string[];
  documentIds: string[];
  attachmentIds: string[];
};

export type UpdateMapTemplateContentRequest = {
  type: TemplateType.MAP;
  selected: MapTemplateSetting[];
};

export type SaveTemplateResponse = { templateId: string };

export type TemplateDTO = {
  id: string;
  name: string;
  description?: string;
  createdByName?: string;
  createdAt: string;
  company?: CompanyItem;
  data: TemplateDataDTO;
};

export type ProjectTemplateDTO = TemplateDTO & { data: ProjectTemplateDataDTO };

export const isProjectTemplate = (template: TemplateDTO): template is ProjectTemplateDTO =>
  template.data.type === TemplateType.PROJECT;

export type MapTemplateDTO = TemplateDTO & { data: MapTemplateDataDTO };

export const isMapTemplate = (template: TemplateDTO): template is MapTemplateDTO =>
  template.data.type === TemplateType.MAP;

export const canSaveTemplates = (
  featureToggles: FeatureToggles,
  account: EnterpriseAccountInfo | undefined,
): boolean => featureToggles.templates === true && account?.isAccountAdmin === true;

export type TemplateDataDTO = ProjectTemplateDataDTO | MapTemplateDataDTO;

export type ProjectTemplateDataDTO = {
  type: TemplateType.PROJECT;
  matters: ProjectTemplateMatterDTO[];
  documents: ProjectTemplateDocumentDTO[];
  excluded: TemplateExclusions;
};

export type MapTemplateDataDTO = {
  type: TemplateType.MAP;
  graphType: MapTemplateGraphType;
  settings: MapTemplateSetting[];
  excluded: MapTemplateSetting[];
};

export type TemplateExclusions = {
  decisionIds: string[];
  documentIds: string[];
  attachmentIds: string[];
};

export type ProjectTemplateMatterDTO = {
  id: string;
  type: DecisionType;
  isInformational: boolean;
  texts: LocalizedText[];
  attachments: ProjectTemplateAttachmentDTO[];
};

export type ProjectTemplateDocumentDTO = {
  id: string;
  type: DocumentType;
  isPersonalised: boolean;
  title: LocalizedText[];
  attachments: ProjectTemplateAttachmentDTO[];
};

export type ProjectTemplateAttachmentDTO = { id: string; filename: string };

export const mapTemplateLabels: { [type in MapTemplateGraphType]: TMessageKey } = {
  owner: "ownership.graph-type.owner",
  owns: "ownership.graph-type.owns",
  dual: "ownership.graph-type.dual",
};
